import { timeParse } from "d3";

/**
 * Retrieves historical market data from the API gateway.
 * @param {object} _this - Context variables.
 * @param {object} options - Request parameters.
 * @param {Function} options.fetch - Gateway service function to call, e.g. gatewayService.commodities.technicalAnalysis.
 * @param {object} options.variables - Query parameters passed to `fetch`.
 * @param {string} [options.source] - Semantic label for the data (only used to branch business logic below).
 * @param {boolean} [addToday=true] - Whether to add today's data adjustments.
 * @param {boolean|array} [accumDataKeys=false] - Whether to accumulate data from multiple keys.
 * @returns {Promise<object|array>}
 */
export function getHistoricalData(
  _this,
  { fetch, variables, source },
  addToday = true,
  accumDataKeys = false,
) {
  return fetch(variables).then((response) => {
      if (!response) throw new Error("Error from getHistoricalData");
      let data = JSON.parse(JSON.stringify(response));
      if (Array.isArray(data)) {
        data.forEach((d) => {
          d.date = timeParse("%Y-%m-%d")(d.observation_date);
          d.ma40 = d.ma_40;
          if (d.open == null) d.open = d.close;
          if (d.high == null) d.high = Math.max(d.open, d.close);
          if (d.low == null) d.low = Math.min(d.open, d.close);
        });
        if (
          (_this.selectedAssetType !== 2) &
          (source != "interest_rate_historical")
        ) {
          data = deleteDataRepeatAndAfterDate(data);
        }
        const lastEntry = data[data.length - 1];
        lastEntry.high = Math.max(lastEntry.high, lastEntry.open, lastEntry.close);
        lastEntry.low = Math.min(lastEntry.low, lastEntry.open, lastEntry.close);

        if (addToday) {
          updateMissingData(data, lastEntry);
        }
      } else if (!accumDataKeys) {
        normalizeSingleDataEntry(data);
      }
      if (Array.isArray(accumDataKeys) && !Array.isArray(data)) {
        data = JSON.parse(data);
        if (validateDataRepeat(accumDataKeys, data)) {
          accumDataKeys.push(data);
        }
      }
      if (typeof data === "string") {
        data = JSON.parse(data);
      }
      _this.faildGetHistoricalData = false;
      return data;
    });
}

/**
 * Retrieves a simplified version of historical market data.
 * @param {object} _this - Context variables.
 * @param {object} options - Request parameters.
 * @param {Function} options.fetch - Gateway service function to call.
 * @param {object} options.variables - Query parameters passed to `fetch`.
 * @returns {Promise<array>} - Simplified historical data.
 */
export function getHistoricalDataSimplified(
  _this,
  { fetch, variables },
) {
  return fetch(variables).then((response) => {
    let data = JSON.parse(JSON.stringify(response));
    data.forEach((d) => {
      d.date = timeParse("%Y-%m-%d")(d.observation_date);
    });
    data = deleteDataRepeatAndAfterDate(data);
    const lastEntry = data[data.length - 1];
    lastEntry.high = Math.max(lastEntry.high, lastEntry.open, lastEntry.close);
    lastEntry.low = Math.min(lastEntry.low, lastEntry.open, lastEntry.close);
    return data;
  });
}

function updateMissingData(data, lastEntry) {
  const previousEntry = data[data.length - 2];
  const fieldsToCheck = [
    "interp_trend_short",
    "interp_trend_medium",
    "interp_trend_long",
  ];
  fieldsToCheck.forEach((field) => {
    if (lastEntry[field] == null) {
      lastEntry[field] = previousEntry?.[field];
    }
  });
  if (lastEntry.ma20 == null || lastEntry.ma40 == null) {
    lastEntry.ma20 = calculateMovingAverage(data, 20);
    lastEntry.ma40 = calculateMovingAverage(data, 40);
  }
  if (lastEntry["vol-ewma"] == null || lastEntry["vol-ewma"] === 0) {
    lastEntry["vol-ewma"] = previousEntry?.["vol-ewma"];
  }
}

function normalizeSingleDataEntry(data) {
  ["close", "last_price", "last_close", "last", "high", "low", "open"].forEach(
    (key) => {
      if (data[key] != null) data[key] = +data[key];
    },
  );
}

function validateDataRepeat(dataKeys, data) {
  return dataKeys.every(
    (obj) => obj.continuous_contract !== data.continuous_contract,
  );
}

function calculateMovingAverage(data, n) {
  return data.slice(-n).reduce((sum, entry) => sum + (entry.close || 0), 0) / n;
}

function deleteDataRepeatAndAfterDate(data) {
  const chicagoTZ = "America/Chicago";
  const currentDate = new Date();
  const chicagoCurrentDate = new Date(
    currentDate.toLocaleString("en-US", { timeZone: chicagoTZ }),
  );
  const date = new Set();
  let newData = [];
  data.forEach((d) => {
    const newDate = new Date(d.date);
    if (
      (!date.has(+d.date) && +newDate <= +chicagoCurrentDate) ||
      (+newDate > +chicagoCurrentDate && chicagoCurrentDate.getHours() >= 20)
    ) {
      date.add(+d.date);
      newData.push(d);
    }
  });
  return newData;
}
